import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import { pluralize } from 'inflection';
import React from 'react';
import * as styles from './MentionsTab.module.scss';
import { Field, FieldSet } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { MentionType } from '../../graphql/generated/graphql';
import { IntegrationFeatureStatus } from '../../graphql/generated/graphql';
import type { IntegrationPlatformInfo } from '../../integrations/IntegrationPlatformInfo';
import { MentionMode } from '../../integrations/IntegrationPlatformInfo';

const PARENTS_QUERY = gql(/** @complexity 4 */ `
  query getParents($type: MentionType!) {
    parents: mentionParents(type: $type) {
      id
      name
    }
  }
`);

interface ParentCheckboxesProps {
  platform: string;
  platformName: string;
  platformInfo: IntegrationPlatformInfo;
}

const ParentCheckboxes = ({
  platform,
  platformName,
  platformInfo,
}: ParentCheckboxesProps) => {
  const { mention } = platformInfo;

  const mentionType = `${platform}_${
    mention?.mentionName ?? ''
  }`.toUpperCase() as MentionType;

  const { loading, error, data } = useQuery(PARENTS_QUERY, {
    variables: { type: mentionType },
  });

  if (error) {
    return (
      <>{`We encountered an error when retrieving
    ${pluralize(mention?.parentName ?? '')}.
    Please re-connect ${platformName} and then retry.`}</>
    );
  }

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  const { parents } = data;

  if (!parents.length) {
    return (
      <>{`We could not find any ${platformName} ${pluralize(
        mention?.parentName ?? '',
      )}`}</>
    );
  }

  return (
    <>
      {parents.map((parent) => (
        <Field
          type="checkbox"
          name="enabledParents"
          key={parent.id}
          value={parent.id}
          label={parent.name}
        />
      ))}
    </>
  );
};

const INTEGRATION_FRAGMENT = gql(`
  fragment MentionsTab_integration on Integration {
    id
    platform
    features {
      mention
      teamSearch
    }
    settings {
      enabledParents
      enableAllParents
    }
  }
`);

interface MentionsTabProps {
  integration: DocumentType<typeof INTEGRATION_FRAGMENT>;
  platformInfo: IntegrationPlatformInfo;
}

const MentionsTab = ({ integration, platformInfo }: MentionsTabProps) => {
  const { platform } = integration;
  const isMentionEnabled =
    integration.features?.mention === IntegrationFeatureStatus.Enabled;
  const isTeamSearchEnabled =
    integration.features?.teamSearch === IntegrationFeatureStatus.Enabled;

  const { values } =
    useFormikContext<ReturnType<typeof MentionsTab.getFormValues>>();

  const { mention, name: platformName } = platformInfo;

  if (mention?.parentDisabled) {
    return null;
  }

  let label;

  if (
    mention?.mentionModes?.includes(MentionMode.Embed) &&
    mention?.mentionModes?.includes(MentionMode.Mention)
  ) {
    label = `Enable mentions and embeds for`;
  } else if (mention?.mentionModes?.includes(MentionMode.Embed)) {
    label = `Enable ${mention?.mentionName ?? ''} embeds for`;
  } else {
    label = `Enable ${mention?.mentionName ?? ''} mentions for`;
  }

  return (
    <FieldSet legend={label}>
      <Field
        label={`All ${pluralize(mention?.parentName ?? '')}`}
        name="enableAllParents"
        type="radio"
        value="on"
      />
      <Field
        label={`Selected ${pluralize(mention?.parentName ?? '')}`}
        name="enableAllParents"
        type="radio"
        value="off"
      />
      {(isMentionEnabled || isTeamSearchEnabled) &&
        values?.enableAllParents === 'off' && (
          <div className={styles.checkboxes}>
            <div className="checkbox-list">
              <ParentCheckboxes
                platform={platform}
                platformName={platformName}
                platformInfo={platformInfo}
              />
            </div>
          </div>
        )}
    </FieldSet>
  );
};

MentionsTab.getFormValues = ({
  settings,
}: DocumentType<typeof INTEGRATION_FRAGMENT>) => ({
  enabledParents: settings?.enabledParents || [],
  enableAllParents: settings?.enableAllParents ? 'on' : 'off',
});
MentionsTab.getNewSettings = (
  values: ReturnType<typeof MentionsTab.getFormValues>,
) => ({
  enabledParents: values.enabledParents,
  enableAllParents: values.enableAllParents === 'on',
});

export default MentionsTab;
